export type DurationMode = "hhmm" | "decimal" | "minutes";

export const DURATION_MODES: DurationMode[] = ["hhmm", "decimal", "minutes"];

export function nextDurationMode(mode: DurationMode): DurationMode {
    const index = DURATION_MODES.indexOf(mode);
    return DURATION_MODES[(index + 1) % DURATION_MODES.length];
}

export function formatDuration(
    minutes: number | null | undefined,
    mode: DurationMode,
): string {
    if (minutes === null || minutes === undefined || Number.isNaN(minutes)) {
        return "";
    }

    const total = Math.max(0, Math.round(minutes));

    if (mode === "minutes") {
        // Unit is conveyed by the mode toggle — keep the value bare.
        return String(total);
    }

    if (mode === "decimal") {
        const value = total / 60;
        const rounded = Math.round(value * 100) / 100;
        // Same rationale as minutes: the mode toggle shows "h".
        return String(rounded);
    }

    // hhmm: inline units are part of the format.
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h === 0) {
        return `${m}m`;
    }
    if (m === 0) {
        return `${h}h`;
    }
    return `${h}h${m.toString().padStart(2, "0")}`;
}

/**
 * Parse any supported duration string back to integer minutes. Returns null
 * on unparseable input and on negatives.
 *
 * Accepted shapes (unit-annotated values always win over mode hints):
 *   "150m" / "150min" / "150mins"  → 150m
 *   "2.5h" / "2h" / ".5h"          → hours
 *   "2h30" / "2h30m" / "2h30min"   → hours + extra minutes
 *   "2:30" / ":30"                 → H:MM
 *
 * Bare numbers fall back to the current display mode:
 *   modeHint=minutes → "150" = 150m
 *   modeHint=decimal → "2.5" = 2.5h
 *   modeHint=hhmm    → "2"   = 2h
 */
export function parseDuration(raw: string, modeHint: DurationMode = "hhmm"): number | null {
    const input = raw.trim().toLowerCase().replace(/\s+/g, "");
    if (input === "") {
        return null;
    }

    // Reject negatives outright.
    if (input.startsWith("-")) {
        return null;
    }

    // ":MM" (shorthand for 0h MM)
    const leadingColon = input.match(/^:(\d{1,2})$/);
    if (leadingColon) {
        const m = Number(leadingColon[1]);
        return Number.isFinite(m) ? m : null;
    }

    // "H:MM"
    const colonMatch = input.match(/^(\d+):(\d{1,2})$/);
    if (colonMatch) {
        return Number(colonMatch[1]) * 60 + Number(colonMatch[2]);
    }

    // "Xh", "X.Yh", "XhY", "XhYm", "XhYmin"
    const hmMatch = input.match(/^(\d+(?:\.\d+)?|\.\d+)h(\d+)?(?:m(?:ins?)?)?$/);
    if (hmMatch) {
        const hours = Number(hmMatch[1]);
        const extraMinutes = hmMatch[2] ? Number(hmMatch[2]) : 0;
        if (!Number.isFinite(hours) || !Number.isFinite(extraMinutes)) {
            return null;
        }
        return Math.round(hours * 60 + extraMinutes);
    }

    // Pure minutes: "150m", "150min", "150mins"
    const minMatch = input.match(/^(\d+(?:\.\d+)?|\.\d+)m(?:ins?)?$/);
    if (minMatch) {
        const value = Number(minMatch[1]);
        return Number.isFinite(value) ? Math.round(value) : null;
    }

    // Bare number — interpret per modeHint.
    const bareMatch = input.match(/^(\d+(?:\.\d+)?|\.\d+)$/);
    if (bareMatch) {
        const value = Number(bareMatch[1]);
        if (!Number.isFinite(value)) {
            return null;
        }
        if (modeHint === "minutes") {
            return Math.round(value);
        }
        return Math.round(value * 60);
    }

    return null;
}
